import React, { useEffect, useState } from 'react';
import {
  Box,
  Avatar,
  Typography,
  Button,
  Divider,
  useMediaQuery
} from '@mui/material';
import { useParams } from 'react-router-dom';
import RedeemIcon from '@mui/icons-material/Redeem';
import axios from 'axios';

import ProductList from './ProductList';
import ResendOfferList from './ResendOfferList';
import NavbarBack from './NavbarBack';

const PublicResellerProfile = () => {
  const { id } = useParams();
  const [reseller, setReseller] = useState(null);
  const [isFollowing, setIsFollowing] = useState(false);
  const [offers, setOffers] = useState([]);
  const [showOffers, setShowOffers] = useState(false);
  const loggedInUsername = localStorage.getItem('username');
  const isMobile = useMediaQuery('(max-width:600px)');

  useEffect(() => {
    fetch(`https://reviewhub-lc.onrender.com/api/resellers/${id}`)
      .then(res => res.json())
      .then(data => {
        setReseller(data);

        // Fetch reviews
        return fetch(`https://reviewhub-lc.onrender.com/api/reviews/reseller/${data.resellerId}`)
          .then(res => res.json())
          .then(reviews => {
            fetch(`https://reviewhub-lc.onrender.com/api/auth/reseller-followers/${data.resellerId}`)
              .then(res => res.json())
              .then(followerData => {
                setReseller(prev => ({
                  ...prev,
                  reviews,
                  followerCount: followerData.followerCount,
                }));

                // Fetch offers
                axios
                  .get(`https://reviewhub-lc.onrender.com/api/resendoffers?resellerId=${data.resellerId}`)
                  .then(res => setOffers(res.data))
                  .catch(() => setOffers([]));
              });
          });
      });
  }, [id]);

  useEffect(() => {
    if (reseller && loggedInUsername) {
      fetch('https://reviewhub-lc.onrender.com/api/auth/get-following', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: loggedInUsername }),
      })
        .then(res => res.json())
        .then(data => {
          const following = data.followingResellers || [];
          setIsFollowing(following.map(String).includes(String(reseller.resellerId)));
        });
    }
  }, [reseller, loggedInUsername]);

  const handleToggleFollow = () => {
    fetch('https://reviewhub-lc.onrender.com/api/auth/toggle-follow', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username: loggedInUsername,
        resellerId: reseller.resellerId,
      }),
    })
      .then(res => res.json())
      .then(data => {
        const following = data.followingResellers || [];
        const nowFollowing = following.map(String).includes(String(reseller.resellerId));
        setIsFollowing(nowFollowing);

        fetch(`https://reviewhub-lc.onrender.com/api/auth/reseller-followers/${reseller.resellerId}`)
          .then(res => res.json())
          .then(followerData => {
            setReseller(prev => ({
              ...prev,
              followerCount: followerData.followerCount,
            }));
          });

        alert(
          nowFollowing
            ? `You are now following ${reseller.resellerId}`
            : `You have unfollowed ${reseller.resellerId}`
        );
      });
  };

  if (!reseller) return <Typography>Loading...</Typography>;

  return (
    <>
      <NavbarBack />
      <Box sx={{ p: isMobile ? 1 : 4 }}>
        {/* Profile Section */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: 'row', 
          alignItems: 'flex-start',
          gap: isMobile ? 1 : 4,
          mb: isMobile ? 1 : 4
        }}>
          {/* Avatar - Always on left */}
          <Avatar
            src={reseller.profileImageUrl || '/assets/defaultProfile.jpg'}
            alt="Profile"
            sx={{ 
              width: isMobile ? 60 : 120, 
              height: isMobile ? 60 : 120,
              flexShrink: 0
            }}
          />

          {/* Profile Info */}
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            flexGrow: 1,
            width: '100%'
          }}>
            <Typography variant={isMobile ? 'h6' : 'h5'} fontWeight="bold">
              {reseller.resellerId}
            </Typography>
            <Typography variant={isMobile ? 'body2' : 'subtitle1'} sx={{ color: 'text.secondary' }}>
              {reseller.name}
            </Typography>

            {/* Stats + Follow Button */}
            <Box
              display="flex"
              gap={isMobile ? 1.5 : 3}
              mt={1.5}
              alignItems="center"
              flexWrap="wrap"
            >
              <Typography
                onClick={() => setShowOffers(false)}
                sx={{ 
                  cursor: 'pointer',
                  fontSize: isMobile ? '0.7rem' : '0.875rem'
                }}
              >
                <strong>{reseller.reviews?.length || 0}</strong> Reviews
              </Typography>
              <Typography sx={{ fontSize: isMobile ? '0.7rem' : '0.875rem' }}>
                <strong>{reseller.followerCount || 0}</strong> Followers
              </Typography>

              {loggedInUsername && (
                <Button
                  variant="contained"
                  size="small"
                  sx={{
                    textTransform: 'none',
                    fontSize: isMobile ? '0.65rem' : '0.85rem',
                    py: isMobile ? 0.3 : 0.5,
                    px: isMobile ? 1 : 1.5,
                     backgroundColor: '#e6395f',

                  }}
                  onClick={handleToggleFollow}
                >
                  {isFollowing ? 'Unfollow' : 'Follow'}
                </Button>
              )}

              {loggedInUsername && isFollowing && offers.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    px: isMobile ? 0.8 : 1.5,
                    py: isMobile ? 0.3 : 0.5,
                    borderRadius: 2,
                    backgroundColor: '#fff8e1',
                    animation: 'glow 1.5s ease-in-out infinite alternate',
                    '@keyframes glow': {
                      from: { boxShadow: '0 0 5px gold' },
                      to: { boxShadow: '0 0 20px gold, 0 0 30px orange' },
                    },
                    cursor: 'pointer',
                  }}
                  onClick={() => setShowOffers(true)}
                >
                  <RedeemIcon sx={{ color: 'gold', fontSize: isMobile ? 16 : 20 }} />
                  <Typography
                    fontSize={isMobile ? 10 : 13}
                    fontWeight="bold"
                    color="goldenrod"
                  >
                    Offer
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        {/* Bio - Full width in mobile */}
        <Box sx={{ width: '100%', mt: isMobile ? 0 : 1 }}>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: 'pre-line',
              color: 'text.secondary',
              fontSize: isMobile ? '0.75rem' : '1rem',
              lineHeight: 1.5
            }}
          >
            {reseller.bio || 'No bio available.'}
          </Typography>
        </Box>

        <Divider sx={{ my: isMobile ? 2 : 4 }} />

        {/* Show either offers or product list */}
        {showOffers ? (
          <>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              flexDirection={isMobile ? 'column' : 'row'}
              gap={isMobile ? 1 : 0}
            >
              <Typography variant="h6">
                Offers by {reseller.resellerId}
              </Typography>
            </Box>

            <Box mt={2}>
              <ResendOfferList resellerId={reseller.resellerId} hideControls={true} />
            </Box>
          </>
        ) : (
          <>
            <Typography variant="h6" gutterBottom>
              Reviews
            </Typography>

            <Box mt={2}>
              <ProductList resellerId={reseller.resellerId} showFilters={false} />
            </Box>
          </>
        )}
      </Box>
    </>
  );
};

export default PublicResellerProfile;